import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Download, Share2, ChevronLeft, ChevronRight, Loader2, Image as ImageIcon } from 'lucide-react';
import { api } from '@/hooks/useAuth';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { useAppointmentTypes } from '@/hooks/useAppointmentTypes';

export default function WeeklyExportDialog({ open, onClose, teams }) {
  const { getTypeInfo } = useAppointmentTypes();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    return new Date(today.setDate(diff));
  });
  const [appointments, setAppointments] = useState([]);
  const scheduleRef = useRef(null);

  // Get week dates
  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();
  const weekEnd = weekDates[6];

  // Fetch appointments for the week
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!open) return;
      setLoading(true);
      try {
        // Fetch appointments for both months if week spans two months
        const startMonth = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}`;
        const endMonth = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, '0')}`;
        
        const response1 = await api.get('/appointments', { params: { month: startMonth } });
        let allAppointments = response1.data;
        
        if (startMonth !== endMonth) {
          const response2 = await api.get('/appointments', { params: { month: endMonth } });
          allAppointments = [...allAppointments, ...response2.data];
        }
        
        // Filter to only this week's appointments
        const weekAppointments = allAppointments.filter(apt => {
          const aptDate = new Date(apt.date + 'T00:00:00');
          return aptDate >= weekStart && aptDate <= weekEnd;
        });
        
        setAppointments(weekAppointments);
      } catch (error) {
        toast.error('Failed to load appointments');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAppointments();
  }, [open, weekStart]);

  const previousWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(weekStart.getDate() - 7);
    setWeekStart(newStart);
  };

  const nextWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(weekStart.getDate() + 7);
    setWeekStart(newStart);
  };

  const getTeamInfo = (teamId) => {
    return teams.find(t => t.id === teamId) || { name: 'Unknown', color: '#64748b' };
  };

  const getAppointmentsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments
      .filter(apt => apt.date === dateStr)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const formatDateShort = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatWeekRange = () => {
    const startStr = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  const exportAsImage = async () => {
    if (!scheduleRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(scheduleRef.current, {
        backgroundColor: '#ffffff',
        scale: 4, // High scale to survive WhatsApp compression
        useCORS: true,
        logging: false,
        imageTimeout: 0,
      });
      
      // Convert to JPEG with high quality
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `schedule-${weekStart.toISOString().split('T')[0]}.jpg`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Schedule exported! You can now share it on WhatsApp');
      }, 'image/jpeg', 0.95); // JPEG with 95% quality
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export schedule');
    } finally {
      setExporting(false);
    }
  };

  const shareImage = async () => {
    if (!scheduleRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(scheduleRef.current, {
        backgroundColor: '#ffffff',
        scale: 4, // High scale to survive WhatsApp compression
        useCORS: true,
        logging: false,
        imageTimeout: 0,
      });
      
      canvas.toBlob(async (blob) => {
        const file = new File([blob], `schedule-${weekStart.toISOString().split('T')[0]}.jpg`, { type: 'image/jpeg' });
        
        if (navigator.share && navigator.canShare({ files: [file] })) {
          // Use native share on mobile
          await navigator.share({
            files: [file],
            title: '911 Pro Home Schedule',
            text: `Weekly Schedule: ${formatWeekRange()}`
          });
          toast.success('Shared successfully!');
        } else {
          // Fallback to download
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `schedule-${weekStart.toISOString().split('T')[0]}.jpg`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
          toast.success('Schedule downloaded! Open it and share via WhatsApp');
        }
      }, 'image/jpeg', 0.95); // JPEG with 95% quality
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to share schedule');
    } finally {
      setExporting(false);
    }
  };

  const totalAppointments = appointments.length;
  const scheduledCount = appointments.filter(a => a.status !== 'completed').length;
  const completedCount = appointments.filter(a => a.status === 'completed').length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="weekly-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-[#2563eb]" />
            Export Weekly Schedule
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Week Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" onClick={previousWeek}>
              <ChevronLeft size={16} />
            </Button>
            <span className="font-semibold text-lg">{formatWeekRange()}</span>
            <Button variant="outline" size="sm" onClick={nextWeek}>
              <ChevronRight size={16} />
            </Button>
          </div>

          {/* Export Buttons */}
          <div className="flex gap-2 mb-4">
            <Button 
              onClick={exportAsImage} 
              disabled={exporting || loading}
              className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8]"
            >
              {exporting ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Download Image
            </Button>
            <Button 
              onClick={shareImage} 
              disabled={exporting || loading}
              variant="outline"
              className="flex-1"
            >
              {exporting ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Share2 size={16} className="mr-2" />
              )}
              Share
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
            </div>
          ) : (
            /* Schedule Preview - This will be exported as image */
            <div 
              ref={scheduleRef}
              className="bg-white p-4 rounded-lg border border-slate-200"
              style={{ minWidth: '600px' }}
            >
              {/* Header */}
              <div className="text-center mb-4 pb-3 border-b-2 border-[#2563eb]">
                <h1 className="text-xl font-bold text-[#0f172a]">911 Pro Home Schedule</h1>
                <p className="text-slate-600 text-sm mt-1">{formatWeekRange()}</p>
                <div className="flex justify-center gap-3 mt-2">
                  <Badge variant="secondary">{totalAppointments} Appointments</Badge>
                  {scheduledCount > 0 && <Badge className="bg-amber-100 text-amber-700">{scheduledCount} Scheduled</Badge>}
                  {completedCount > 0 && <Badge className="bg-green-100 text-green-700">{completedCount} Completed</Badge>}
                </div>
              </div>

              {/* Weekly Grid */}
              <div className="space-y-3">
                {weekDates.map((date, index) => {
                  const dayAppointments = getAppointmentsForDate(date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  
                  return (
                    <div 
                      key={index}
                      className={`rounded-lg p-3 ${isToday ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50'}`}
                    >
                      {/* Day Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`font-bold text-base ${isToday ? 'text-[#2563eb]' : 'text-slate-900'}`}>
                            {formatDateShort(date)}
                          </span>
                          {isToday && <Badge className="bg-[#2563eb] text-white text-xs font-semibold">Today</Badge>}
                        </div>
                        <span className="text-sm text-slate-700 font-medium">
                          {dayAppointments.length} appointment{dayAppointments.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Appointments */}
                      {dayAppointments.length === 0 ? (
                        <p className="text-sm text-slate-500 italic">No appointments</p>
                      ) : (
                        <div className="space-y-1">
                          {dayAppointments.map((apt) => {
                            const team = getTeamInfo(apt.team_id);
                            const isCompleted = apt.status === 'completed';
                            const typeInfo = getTypeInfo(apt.appointment_type);
                            const TypeIcon = typeInfo.icon;
                            
                            return (
                              <div 
                                key={apt.id}
                                className={`flex flex-wrap items-start gap-2 text-sm p-2 rounded ${
                                  isCompleted ? 'bg-green-100/50' : ''
                                }`}
                                style={{ backgroundColor: isCompleted ? undefined : typeInfo.bgColor }}
                              >
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <div 
                                    className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: typeInfo.color }}
                                  >
                                    <TypeIcon size={12} className="text-white" />
                                  </div>
                                  <span className="font-mono text-xs text-slate-700 font-medium">
                                    {apt.start_time}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className={`font-medium block ${isCompleted ? 'line-through text-slate-500' : 'text-slate-800'}`}>
                                    {apt.title}
                                  </span>
                                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    <span className="text-xs text-slate-600 font-medium">
                                      {team.name}
                                    </span>
                                    <span className="text-xs font-medium" style={{ color: typeInfo.color }}>
                                      • {typeInfo.label}
                                    </span>
                                    {apt.assigned_to_user_name && (
                                      <span className="text-xs text-blue-800 font-semibold">
                                        → {apt.assigned_to_user_name}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="mt-4 pt-3 border-t border-slate-300 text-center">
                <p className="text-sm text-slate-600 font-medium">
                  Generated on {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
